import { LoadingStageModel, ValueModel } from '@kts-front/models';
import { BaseResponse, Nullable } from '@kts-front/types';
import { computed, makeObservable, runInAction } from 'mobx';

import { API_ERROR_TYPE } from 'types/ApiError';
import { isDefined } from 'utils/isDefined';

import { appConfigService } from './AppConfigService';
import { defaultAppConfig } from './config';
import { AppConfig, AppConfigFetchErrorData, IAppConfigReady, Images, LogicConfig, UIConfig } from './types';

export class AppConfigStore implements IAppConfigReady {
  private _configModel = new ValueModel<Nullable<AppConfig>>(null);
  private _loading = new LoadingStageModel();
  private _error = new ValueModel<Nullable<string>>(null);

  constructor() {
    makeObservable(this, {
      config: computed,
      isLoading: computed,
      isReady: computed,
      error: computed,
      ui: computed,
      images: computed,
      logic: computed,
    });
  }

  get isReady(): boolean {
    return this._configModel.value !== null;
  }

  get config(): AppConfig {
    return this._loadedConfig;
  }

  get isLoading(): boolean {
    return this._loading.isLoading || this._loading.isNotStarted;
  }

  get error(): Nullable<string> {
    return this._error.value;
  }

  get ui(): UIConfig {
    return this._loadedConfig.ui;
  }

  get images(): Images {
    return this._loadedConfig.images;
  }

  get logic(): LogicConfig {
    return this._loadedConfig.logic;
  }

  private get _loadedConfig(): AppConfig {
    const config = this._configModel.value;

    if (!config) {
      throw new Error('AppConfig is not loaded');
    }

    return config;
  }

  async fetchConfig(): Promise<BaseResponse> {
    this._loading.loading();

    const response = await appConfigService.fetchData();

    return runInAction(() => {
      if (response.isError) {
        if (response.data.errorType === API_ERROR_TYPE.companyBlocked) {
          this._configModel.change(defaultAppConfig);
          this._loading.success();

          return { isError: false };
        }

        this._loading.error();

        this._error.change(this._formatFetchError(response.data));

        return { isError: true };
      }

      this._configModel.change(response.data);
      this._loading.success();

      return { isError: false };
    });
  }

  private _formatFetchError({ parsingErrors, httpStatusCode }: AppConfigFetchErrorData): string {
    if (parsingErrors.length > 0) {
      return `Ошибка валидации глобальной конфигурации (${parsingErrors.map((error) => `${error.path}: ${error.message}`).join(', ')}). Обратитесь к администратору.`;
    }

    const statusCodeText = isDefined(httpStatusCode) ? String(httpStatusCode) : 'неизвестен';

    return `Ошибка загрузки глобальной конфигурации. Обратитесь к администратору. Код ошибки: ${statusCodeText}`;
  }
}
